
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const INITIAL_IMAGE_BATCH = 15; // Load first 15 images initially
const UNAUTHENTICATED_IMAGE_LIMIT = 6; // Non-authenticated users see only 6 images

type PropertyDetails = {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  price: number;
  arv_value: number | null;
  gross_margin: number | null;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  lot_size: string | null;
  year_built: number | null;
  foundation_type: string | null;
  roof_age: string | null;
  windows_age: string | null;
  window_type: string | null;
  furnace_age: string | null;
  water_heater_age: string | null;
  architecture_style: string | null;
  property_type: string | null;
  parking_type: string | null;
  description: string | null;
  zillow_link: string | null;
  status: string;
  created_at: string | null;
  contract_expiration_date: string | null;
  seller_id: string;
  video_url: string | null;
  video_thumbnail_url: string | null;
  closed_date: string | null;
  sale_price: number | null;
  financing_type: string | null;
}

type SellerInfo = {
  id: string;
  full_name: string | null;
  business_name: string | null;
  phone_number: string | null;
  email: string | null;
  profile_image_url: string | null;
  hide_gross_margin?: boolean | null;
}

type PublicSellerInfo = {
  id: string;
  full_name: string | null;
  business_name: string | null;
  profile_image_url: string | null;
  hide_gross_margin?: boolean | null;
}

export const usePropertyDetail = (id: string | undefined) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [property, setProperty] = useState<PropertyDetails | null>(null);
  const [propertyImages, setPropertyImages] = useState<string[]>([]);
  const [sellerInfo, setSellerInfo] = useState<SellerInfo | null>(null);
  const [daysOnSite, setDaysOnSite] = useState(0);
  const [totalImageCount, setTotalImageCount] = useState(0);
  const [loadedImageCount, setLoadedImageCount] = useState(0);
  const [isLoadingMoreImages, setIsLoadingMoreImages] = useState(false);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        
        // Fetch property details
        const { data: propertyData, error: propertyError } = await supabase
          .from('properties')
          .select('*')
          .eq('id', id)
          .single();
          
        if (propertyError) {
          console.error("Error fetching property:", propertyError);
          // Don't show toast - the page will display an appropriate message
          return;
        }
        
        if (!propertyData) {
          // Don't show toast - the page will display an appropriate message
          return;
        }
        
        setProperty({
          ...propertyData,
          windows_age: null,
          window_type: (propertyData as any).window_type || null,
          video_url: (propertyData as any).video_url || null,
          video_thumbnail_url: (propertyData as any).video_thumbnail_url || null,
          closed_date: (propertyData as any).closed_date || null,
          sale_price: (propertyData as any).sale_price || null,
          financing_type: (propertyData as any).financing_type || null
        } as PropertyDetails);
        
        // Calculate days on site
        if (propertyData.created_at) {
          const createdDate = new Date(propertyData.created_at);
          const currentDate = new Date();
          const diffTime = Math.abs(currentDate.getTime() - createdDate.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setDaysOnSite(diffDays);
        }
        
        // Get total image count first
        const { count: totalCount } = await supabase
          .from('property_images')
          .select('*', { count: 'exact', head: true })
          .eq('property_id', id);
        
        setTotalImageCount(totalCount || 0);
        
        // Fetch property images with initial batch
        const { data: imagesData, error: imagesError } = await supabase
          .from('property_images')
          .select('*')
          .eq('property_id', id)
          .order('display_order', { ascending: true })
          .range(0, (user ? INITIAL_IMAGE_BATCH : UNAUTHENTICATED_IMAGE_LIMIT) - 1);
          
        if (imagesError) {
          console.error("Error fetching images:", imagesError);
        } else {
          // Use images from property_images table (respects display_order)
          if (imagesData && imagesData.length > 0) {
            setPropertyImages(imagesData.map(img => img.image_url));
            setLoadedImageCount(imagesData.length);
          } else if (propertyData.image_url) {
            // Fallback to legacy image_url if no property_images exist
            setPropertyImages([propertyData.image_url]);
            setLoadedImageCount(0);
          } else {
            // No images at all - use placeholder
            setPropertyImages(["https://images.unsplash.com/photo-1570129477492-45c003edd2be"]);
            setLoadedImageCount(0);
          }
        }
        
        // Fetch seller information using secure function
        if (propertyData.seller_id) {
          if (user) {
            // Check if authenticated user is the property owner
            if (user.id === propertyData.seller_id) {
              // Property owner viewing their own property - fetch profile directly
              try {
                const { data: ownerProfile, error: ownerError } = await supabase
                  .from('profiles')
                  .select('id, full_name, business_name, phone_number, email, profile_image_url, hide_gross_margin')
                  .eq('id', propertyData.seller_id)
                  .single();
                  
                if (ownerError) {
                  console.error("Error fetching owner profile:", ownerError);
                } else if (ownerProfile) {
                  setSellerInfo(ownerProfile as SellerInfo);
                }
              } catch (err) {
                console.error("Error fetching owner profile:", err);
              }
            } else {
              // Authenticated user (buyer) - get seller contact info through secure function
              try {
                const result = await (supabase as any).rpc('get_seller_contact_for_property', { 
                  p_property_id: id 
                });
                const { data: sellerData, error: sellerError } = result;
                  
                if (sellerError) {
                  console.error("Error fetching seller info:", sellerError);
                } else if (sellerData && Array.isArray(sellerData) && sellerData.length > 0) {
                  setSellerInfo(sellerData[0] as SellerInfo);
                }
              } catch (err) {
                console.error("Error calling seller contact function:", err);
              }
            }
          } else {
            // Unauthenticated user - get limited seller info through public function
            try {
              const result = await (supabase as any).rpc('get_public_seller_info', { 
                seller_user_id: propertyData.seller_id 
              });
              const { data: publicSellerData, error: publicSellerError } = result;
                
              if (publicSellerError) {
                console.error("Error fetching public seller info:", publicSellerError);
              } else if (publicSellerData && Array.isArray(publicSellerData) && publicSellerData.length > 0) {
                // Add null values for restricted fields to maintain type compatibility
                setSellerInfo({
                  id: publicSellerData[0].id,
                  full_name: publicSellerData[0].full_name,
                  business_name: publicSellerData[0].business_name,
                  profile_image_url: publicSellerData[0].profile_image_url,
                  email: null,
                  phone_number: null,
                  hide_gross_margin: publicSellerData[0].hide_gross_margin ?? false
                } as SellerInfo);
              }
            } catch (err) {
              console.error("Error calling public seller function:", err);
            }
          }
        }
      } catch (error) {
        console.error("Unexpected error:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPropertyDetails();
  }, [id, toast, user]);

  const loadMoreImages = useCallback(async () => {
    if (!id || isLoadingMoreImages || !property) return;
    
    setIsLoadingMoreImages(true);
    
    try {
      // Fetch next batch of images
      const { data: moreImagesData, error } = await supabase
        .from('property_images')
        .select('*')
        .eq('property_id', id)
        .order('display_order', { ascending: true })
        .range(loadedImageCount, loadedImageCount + INITIAL_IMAGE_BATCH - 1);
      
      if (error) {
        console.error("Error loading more images:", error);
        toast({
          title: "Error",
          description: "Failed to load more images",
          variant: "destructive"
        });
        return;
      }
      
      if (moreImagesData && moreImagesData.length > 0) {
        const newImages = moreImagesData.map(img => img.image_url);
        setPropertyImages(prev => [...prev, ...newImages]);
        setLoadedImageCount(prev => prev + moreImagesData.length);
        
        toast({
          title: "Images loaded",
          description: `Loaded ${moreImagesData.length} more images`
        });
      }
    } catch (error) {
      console.error("Error loading more images:", error);
      toast({
        title: "Error",
        description: "An error occurred while loading images",
        variant: "destructive"
      });
    } finally {
      setIsLoadingMoreImages(false);
    }
  }, [id, loadedImageCount, isLoadingMoreImages, property, toast]);

  const hasMoreImages = !!user && totalImageCount > loadedImageCount;

  return {
    isLoading,
    property,
    propertyImages,
    sellerInfo,
    daysOnSite,
    totalImageCount,
    loadedImageCount,
    hasMoreImages,
    loadMoreImages,
    isLoadingMoreImages
  };
};
